import { Link } from "react-router-dom";
import {
  ArrowRight,
  AudioLines,
  CheckCircle2,
  ShieldCheck,
  Mic,
  ClipboardList,
  Users,
  BarChart3,
  Lock,
  Briefcase,
  FileSignature,
  CalendarDays,
  Receipt,
  Ruler,
  FolderOpen,
  BadgeCheck,
  PieChart,
} from "lucide-react";
import { LangToggle, Header } from "./landingPrimitives";
import { COPY as SHARED_COPY } from "./landingCopy";

// How It Works — Top Section (Part 1 of 2).
// Live React UI composed over the PO-approved background canvas
// /brand/how-it-works/how_it_works_top_env_canvas.png. The canvas
// contains only environmental artwork (contractor, Levi, house,
// workbench, mug, blueprints, left blue gradient). Every text
// element, card, overlay, icon, and bottom strip on this section
// is live React — no UI is baked into the image.
//
// English is the only fully-authored copy in this Pass 1
// implementation. `es` mirrors `en` strings verbatim as
// placeholders so the Spanish toggle does not crash — PO Spanish
// copy lands in a later pass and replaces these placeholders.

const COPY_HIW = {
  en: {
    nav: {
      features: "Features",
      how_it_works: "How It Works",
      pricing: "Pricing",
      story: "Our Story",
      signin: "Sign In",
      trial: "Start Free Trial",
    },
    tagline: "The AI Operating System for Contractors",
    headline_a: "Run your jobs",
    headline_b: "by talking to",
    headline_levi: "Levi.",
    subhead: "Your AI coworker on every job.",
    lede:
      "Levi listens, understands, and gets things done — so you can stay focused on your crew and your customers, not the busywork.",
    benefits: [
      {
        title: "Talk naturally.",
        body:
          "Ask, update, or delegate by voice or text — Levi understands what you mean.",
      },
      {
        title: "Get more done.",
        body:
          "Levi handles updates, creates documents, and keeps your projects moving.",
      },
      {
        title: "Keep everyone aligned.",
        body:
          "Levi informs your crew and customers — so nothing slips through the cracks.",
      },
      {
        title: "See what matters.",
        body:
          "Levi organizes your data and shows you exactly what you need to act on.",
      },
      {
        title: "Work with confidence.",
        body:
          "Your data is secure. Your pricing is yours. Levi works for you, not the other way around.",
      },
    ],
    voice_bubble: {
      before: "Levi, send the proposal to ",
      person: "Brian Reynolds",
      after: " and schedule the roof install for next Wednesday.",
    },
    response: {
      name: "Levi",
      lines: [
        "Proposal sent to Brian Reynolds",
        "Roof install scheduled for next Wednesday.",
      ],
      closing: "All set. You’re good to go.",
    },
    right_card: {
      title_line_a: "Levi works across",
      title_line_b: "your entire business",
      rows: [
        { label: "Customers & Projects", Icon: Users },
        { label: "Estimates & Proposals", Icon: FileSignature },
        { label: "Scheduling & Work Orders", Icon: CalendarDays },
        { label: "Invoicing", Icon: Receipt },
        { label: "Material Takeoffs", Icon: Ruler },
        { label: "Documents & Photos", Icon: FolderOpen },
        { label: "Warranties & Follow-Up", Icon: BadgeCheck },
        { label: "Insights & Profitability", Icon: PieChart },
      ],
      footer:
        "Levi helps you manage your business. You control your payments with your preferred provider.",
    },
    strip: [
      { Icon: Mic, title: "Talk or type.", body: "Levi is always ready." },
      {
        Icon: CheckCircle2,
        title: "Delegate with confidence.",
        body: "Levi takes action and gets it done.",
      },
      {
        Icon: Users,
        title: "Stay aligned.",
        body: "Levi keeps your crew and customers in the loop.",
      },
      {
        Icon: BarChart3,
        title: "See the big picture.",
        body: "Levi organizes your data and surfaces what matters.",
      },
      {
        Icon: Lock,
        title: "You’re in control.",
        body: "Your business. Your pricing. Your rules.",
      },
    ],
  },
};
COPY_HIW.es = {
  nav: {
    features: "Funciones",
    how_it_works: "Cómo Funciona",
    pricing: "Precios",
    story: "Nuestra Historia",
    signin: "Iniciar Sesión",
    trial: "Comenzar Gratis",
  },
  tagline: "El Sistema Operativo de IA para Contratistas",
  headline_a: "Maneja tus proyectos",
  headline_b: "hablando con",
  headline_levi: "Levi.",
  subhead: "Tu compañero de IA en cada trabajo.",
  lede:
    "Levi escucha, entiende y se encarga de las cosas — para que puedas mantener el foco en tu equipo y tus clientes, no en el papeleo.",
  benefits: [
    {
      title: "Habla con naturalidad.",
      body:
        "Pregunta, actualiza o delega por voz o texto — Levi entiende lo que quieres decir.",
    },
    {
      title: "Haz más en menos tiempo.",
      body:
        "Levi realiza actualizaciones, crea documentos y mantiene tus proyectos avanzando.",
    },
    {
      title: "Mantén a todos alineados.",
      body:
        "Levi informa a tu equipo y a tus clientes — para que nada se te escape.",
    },
    {
      title: "Ve lo que importa.",
      body:
        "Levi organiza tu información y te muestra exactamente qué necesita tu atención.",
    },
    {
      title: "Trabaja con confianza.",
      body:
        "Tus datos están seguros. Tus precios son tuyos. Levi trabaja para ti, no al revés.",
    },
  ],
  voice_bubble: {
    before: "Levi, envía la propuesta a ",
    person: "Brian Reynolds",
    after:
      " y agenda la instalación de techo para el próximo miércoles.",
  },
  response: {
    name: "Levi",
    lines: [
      "Propuesta enviada a Brian Reynolds",
      "Instalación de techo agendada para el próximo miércoles.",
    ],
    closing: "Listo. Todo en orden.",
  },
  right_card: {
    title_line_a: "Levi trabaja en todo",
    title_line_b: "tu negocio",
    rows: [
      { label: "Clientes y Proyectos", Icon: Users },
      { label: "Estimados y Propuestas", Icon: FileSignature },
      { label: "Programación y Órdenes", Icon: CalendarDays },
      { label: "Facturación", Icon: Receipt },
      { label: "Cálculo de Materiales", Icon: Ruler },
      { label: "Documentos y Fotos", Icon: FolderOpen },
      { label: "Garantías y Seguimiento", Icon: BadgeCheck },
      { label: "Análisis y Rentabilidad", Icon: PieChart },
    ],
    footer:
      "Levi te ayuda a manejar tu negocio. Tú controlas tus pagos con el proveedor de tu preferencia.",
  },
  strip: [
    { Icon: Mic, title: "Habla o escribe.", body: "Levi siempre está listo." },
    {
      Icon: CheckCircle2,
      title: "Delega con confianza.",
      body: "Levi toma acción y lo resuelve.",
    },
    {
      Icon: Users,
      title: "Mantén a todos coordinados.",
      body: "Levi informa a tu equipo y clientes.",
    },
    {
      Icon: BarChart3,
      title: "Ve el panorama completo.",
      body: "Levi organiza tus datos y destaca lo importante.",
    },
    {
      Icon: Lock,
      title: "Tú tienes el control.",
      body: "Tu negocio. Tus precios. Tus reglas.",
    },
  ],
};

const AvatarLevi = () => (
  <div
    className="w-8 h-8 rounded-full overflow-hidden bg-[var(--jc-navy)] flex items-center justify-center flex-shrink-0"
    data-testid="hiw-response-avatar"
  >
    <img
      src="/brand/how-it-works/how_it_works_top_env_canvas.png"
      alt=""
      style={{
        width: "220%",
        height: "220%",
        objectFit: "cover",
        objectPosition: "76% 42%",
      }}
      draggable={false}
    />
  </div>
);

const PublicHeader = ({ c }) => (
  <header
    className="bg-[var(--jc-navy)] text-white relative z-30"
    data-testid="hiw-header"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-16 sm:h-20 flex items-center justify-between gap-4">
      <Link
        to="/"
        aria-label="JobClose AI"
        className="inline-flex items-center gap-2 sm:gap-3 min-w-0"
        data-testid="hiw-logo"
      >
        <img
          src="/brand/jobclose-ai-approved-logo-mark.png"
          alt=""
          className="shrink-0 select-none h-8 sm:h-11 w-auto"
          draggable={false}
        />
        <div className="leading-tight">
          <div className="font-display text-[17px] sm:text-[22px] tracking-tight text-white whitespace-nowrap">
            JobClose <span className="text-[var(--jc-blue)]">AI</span>
          </div>
          <div className="hidden sm:block text-white/60 text-[11px] font-medium">
            {c.tagline}
          </div>
        </div>
      </Link>
      <nav
        className="hidden md:flex items-center gap-9 text-[15px]"
        data-testid="hiw-nav"
      >
        <span className="text-white/80 hover:text-white cursor-default">
          {c.nav.features}
        </span>
        <span
          className="text-white font-semibold relative"
          data-testid="hiw-nav-active"
        >
          {c.nav.how_it_works}
          <span className="absolute -bottom-2 left-0 right-0 h-[3px] bg-[var(--jc-blue)] rounded-full" />
        </span>
        <span className="text-white/80 hover:text-white cursor-default">
          {c.nav.pricing}
        </span>
        <span className="text-white/80 hover:text-white cursor-default">
          {c.nav.story}
        </span>
      </nav>
      <div className="flex items-center gap-2 sm:gap-5 text-[13px] sm:text-[15px]">
        <span className="hidden md:inline text-white/90">{c.nav.signin}</span>
        <button
          type="button"
          className="jc-btn-blue px-3.5 sm:px-5 py-2 sm:py-2.5 rounded-md font-semibold text-[13px] sm:text-[15px] whitespace-nowrap"
          data-testid="hiw-cta"
        >
          {c.nav.trial}
        </button>
      </div>
    </div>
  </header>
);

export const HowItWorksTopSection = ({ lang = "en", setLang = () => {} }) => {
  const c = COPY_HIW[lang] || COPY_HIW.en;
  // Shared Header consumes ``landingCopy.js`` keys (c.nav.how,
  // c.nav.features, c.nav.pricing, c.nav.story, c.nav.demo,
  // c.tagline). HIW's own body copy dictionary uses different
  // keys (c.nav.how_it_works, c.nav.trial, …), so pass the
  // shared-marketing dictionary explicitly to the Header while
  // the section body continues to use ``c`` (COPY_HIW). No
  // translation additions; no other file touched.
  const sharedC = SHARED_COPY[lang] || SHARED_COPY.en;
  return (
    <>
      {/* PO 2026-02 · Mobile public-navigation route-parity fix.
          Swap the private PublicHeader for the shared Header used
          by every other public marketing page so /how-it-works
          inherits the mobile hamburger. Private PublicHeader
          intentionally left in place (dead code) — its removal
          belongs to the Public Website Final Polish pass. */}
      <Header c={sharedC} lang={lang} setLang={setLang} activePage="how" />
      <section
        className="relative bg-white overflow-hidden"
        data-testid="hiw-top-section"
      >
        {/* ================================================================
            DESKTOP LAYOUT (≥ lg / 1024px) — LOCKED PO-approved composition.
            The absolute-positioned overlays only render at desktop widths.
            ================================================================ */}
        <div className="hidden lg:block" data-testid="hiw-desktop-wrap">
        {/* Environmental background canvas — locked, do not edit.
            New approved canvas: contractor + Levi centered with full-bleed
            environment (no left blue safe area). object-position 60% 42%
            centers the composition with a subtle horizontal shift so
            Levi's rightmost edge lands ~x=1600 at desktop 1920, leaving
            a clean ~300px right-side slot for the business card. */}
        <img
          src="/brand/how-it-works/how_it_works_top_env_canvas.png"
          alt=""
          className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
          style={{ objectPosition: "60% 42%" }}
          draggable={false}
          data-testid="hiw-env-canvas"
        />
        {/* Soft light wash on left column area — subtle white gradient
            improves readability of the enlarged benefit rows over the
            new full-bleed environmental photography. NOT redesign;
            just a legibility lift equivalent to the blue safe area
            that the previous canvas had baked in. */}
        <div
          className="absolute z-[1] inset-y-0 left-0 pointer-events-none"
          style={{
            width: 620,
            background:
              "linear-gradient(to right, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.86) 42%, rgba(255,255,255,0.6) 78%, rgba(255,255,255,0) 100%)",
          }}
          aria-hidden="true"
        />
        {/* Mobile lang toggle placeholder (kept invisible in Pass 1) */}
        <div className="hidden">
          <LangToggle lang={lang} setLang={setLang} />
        </div>

        {/* Absolute-positioned content — Pass 3 (Option 4-adjacent):
            expanded scale + card in middle-right clear of Levi */}
        <div className="relative z-10 w-full" style={{ minHeight: 1440 }}>
          {/* LEFT — copy + benefits, enlarged per PO direction */}
          <div
            className="absolute"
            style={{ left: 40, top: 72, width: 480 }}
            data-testid="hiw-left-column"
          >
            <h1
              className="font-display font-extrabold tracking-tight text-[var(--jc-text)] leading-[1.02]"
              style={{ fontSize: "64px" }}
              data-testid="hiw-headline"
            >
              {c.headline_a}
              <br />
              {c.headline_b}{" "}
              <span className="text-[var(--jc-blue)]">
                {c.headline_levi}
              </span>
            </h1>
            <p
              className="mt-6 text-[26px] font-semibold text-[var(--jc-text)]"
              data-testid="hiw-subhead"
            >
              {c.subhead}
            </p>
            <p
              className="mt-5 text-[18px] leading-[1.55] font-medium text-[var(--jc-text)] max-w-[460px]"
              data-testid="hiw-lede"
            >
              {c.lede}
            </p>

            <ul className="mt-11 space-y-6" data-testid="hiw-benefits">
              {c.benefits.map((b, idx) => {
                const Icons = [Mic, CheckCircle2, Users, BarChart3, ShieldCheck];
                const Icon = Icons[idx];
                return (
                  <li
                    key={idx}
                    className="flex items-start gap-4"
                    data-testid={`hiw-benefit-${idx}`}
                  >
                    <span
                      className="mt-0.5 inline-flex items-center justify-center w-12 h-12 rounded-lg bg-[var(--jc-blue)] text-white flex-shrink-0"
                      aria-hidden="true"
                    >
                      <Icon size={24} strokeWidth={2.2} />
                    </span>
                    <div className="min-w-0 max-w-[420px]">
                      <div className="text-[19px] font-bold text-[var(--jc-text)] leading-tight">
                        {b.title}
                      </div>
                      <div className="mt-1.5 text-[15px] leading-[1.5] font-medium text-[var(--jc-text)]">
                        {b.body}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* CENTER — voice bubble, moved slightly lower + right */}
          <div
            className="absolute rounded-2xl px-5 py-4 shadow-2xl border border-white/10"
            style={{
              left: 600,
              top: 820,
              width: 380,
              backgroundColor: "#0A1128",
            }}
            data-testid="hiw-voice-bubble"
          >
            <div className="flex items-start gap-3">
              <span className="mt-1 flex-shrink-0">
                <AudioLines
                  size={22}
                  className="text-[var(--jc-blue)]"
                  strokeWidth={2.4}
                />
              </span>
              <p className="text-[15.5px] leading-[1.45] text-white font-medium">
                {c.voice_bubble.before}
                <span
                  className="text-[var(--jc-blue)] font-semibold"
                  data-testid="hiw-voice-brian"
                >
                  {c.voice_bubble.person}
                </span>
                {c.voice_bubble.after}
              </p>
            </div>
          </div>

          {/* CENTER — Levi response card. Tightened gap to bubble and
              shifted so its TOP aligns with Levi's head/hat level,
              making the response feel like it's coming from Levi. */}
          <div
            className="absolute rounded-2xl bg-white shadow-2xl px-5 py-4 border border-black/5"
            style={{ left: 600, top: 960, width: 420 }}
            data-testid="hiw-response"
          >
            <div className="flex items-center gap-3 mb-3">
              <AvatarLevi />
              <span className="text-[17px] font-bold text-[var(--jc-text)]">
                {c.response.name}
              </span>
            </div>
            <ul className="space-y-2.5">
              {c.response.lines.map((line, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2.5 text-[15px] text-[var(--jc-text)] leading-[1.45]"
                  data-testid={`hiw-response-line-${i}`}
                >
                  <CheckCircle2
                    size={18}
                    className="text-green-600 mt-0.5 flex-shrink-0"
                    strokeWidth={2.4}
                  />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <p
              className="mt-3.5 text-[15px] font-semibold text-green-600"
              data-testid="hiw-response-closing"
            >
              {c.response.closing}
            </p>
          </div>

          {/* RIGHT — "Levi works across your entire business" card,
              brought DOWN so its TOP aligns with Levi's head/hat level
              so it visually connects to Levi instead of floating in
              the corner. Maintains readable full scale. */}
          <div
            className="absolute"
            style={{ right: 20, top: 460, width: 300 }}
            data-testid="hiw-right-column"
          >
            <div
              className="rounded-2xl bg-white shadow-2xl border border-black/5 p-6"
              data-testid="hiw-right-card"
            >
              <h3 className="font-display font-bold text-[19px] leading-tight text-[var(--jc-text)]">
                {c.right_card.title_line_a}
                <br />
                {c.right_card.title_line_b}
              </h3>
              <ul className="mt-5 space-y-3.5" data-testid="hiw-right-rows">
                {c.right_card.rows.map((r, i) => {
                  const Icon = r.Icon;
                  return (
                    <li
                      key={i}
                      className="flex items-center gap-3"
                      data-testid={`hiw-right-row-${i}`}
                    >
                      <span
                        className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[var(--jc-blue-soft)] text-[var(--jc-blue)] flex-shrink-0"
                        aria-hidden="true"
                      >
                        <Icon size={18} strokeWidth={2.2} />
                      </span>
                      <span className="text-[14px] font-semibold text-[var(--jc-text)] leading-tight">
                        {r.label}
                      </span>
                    </li>
                  );
                })}
              </ul>
              <p
                className="mt-5 pt-4 border-t border-black/5 text-[12.5px] leading-[1.5] text-[var(--jc-text-soft)]"
                data-testid="hiw-right-footer"
              >
                {c.right_card.footer}
              </p>
            </div>
          </div>
        </div>

        {/* BOTTOM STRIP — dark navy, 5 cells, enlarged for readability */}
        <div
          className="relative z-10 bg-[#0A1128] text-white"
          data-testid="hiw-bottom-strip"
        >
          <div className="max-w-[1600px] mx-auto px-8 lg:px-12 py-9">
            <div className="grid grid-cols-5 gap-8">
              {c.strip.map((s, i) => {
                const Icon = s.Icon;
                return (
                  <div
                    key={i}
                    className="flex items-start gap-4"
                    data-testid={`hiw-strip-${i}`}
                  >
                    <span
                      className="mt-0.5 inline-flex items-center justify-center w-12 h-12 rounded-lg bg-white/10 text-[var(--jc-blue)] flex-shrink-0"
                      aria-hidden="true"
                    >
                      <Icon size={24} strokeWidth={2.4} />
                    </span>
                    <div className="min-w-0">
                      <div className="text-[17px] font-bold leading-tight">
                        {s.title}
                      </div>
                      <div className="mt-1.5 text-[14.5px] leading-[1.45] font-medium text-white/95">
                        {s.body}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        </div>{/* end hiw-desktop-wrap */}

        {/* ================================================================
            MOBILE LAYOUT (< lg / 1024px) — deliberate vertical stack.
            Header + intro + environmental block + conversation + benefits
            + business card + bottom strip. Uses the SAME approved
            background asset, cropped via CSS for a mobile focal point.
            ================================================================ */}
        <div className="lg:hidden" data-testid="hiw-mobile-wrap">
          {/* 2 · Headline / introduction */}
          <div className="px-5 pt-8 pb-6 bg-white">
            <h1
              className="font-display font-extrabold tracking-tight text-[var(--jc-text)] leading-[1.05] text-[38px]"
              data-testid="hiw-m-headline"
            >
              {c.headline_a}
              <br />
              {c.headline_b}{" "}
              <span className="text-[var(--jc-blue)]">{c.headline_levi}</span>
            </h1>
            <p className="mt-4 text-[19px] font-semibold text-[var(--jc-text)]" data-testid="hiw-m-subhead">
              {c.subhead}
            </p>
            <p className="mt-3 text-[15.5px] leading-[1.5] font-medium text-[var(--jc-text)]" data-testid="hiw-m-lede">
              {c.lede}
            </p>
          </div>

          {/* 3 · Contractor + Levi visual block — cropped from approved bg */}
          <div
            className="relative w-full overflow-hidden"
            style={{ height: 320 }}
            data-testid="hiw-m-env"
          >
            <img
              src="/brand/how-it-works/how_it_works_top_env_canvas.png"
              alt=""
              className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
              style={{ objectPosition: "56% 44%" }}
              draggable={false}
            />
          </div>

          {/* 4 · Contractor → Levi conversation (stacked cards) */}
          <div className="px-5 pt-6 pb-8 bg-white space-y-3" data-testid="hiw-m-convo">
            <div className="rounded-2xl px-4 py-3.5 shadow-lg border border-white/10 bg-[#0A1128]" data-testid="hiw-m-voice">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 flex-shrink-0">
                  <AudioLines size={20} className="text-[var(--jc-blue)]" strokeWidth={2.4} />
                </span>
                <p className="text-[14.5px] leading-[1.45] text-white font-medium">
                  {c.voice_bubble.before}
                  <span className="text-[var(--jc-blue)] font-semibold">{c.voice_bubble.person}</span>
                  {c.voice_bubble.after}
                </p>
              </div>
            </div>
            <div className="rounded-2xl bg-white shadow-lg px-4 py-3.5 border border-black/10" data-testid="hiw-m-response">
              <div className="flex items-center gap-2.5 mb-2">
                <AvatarLevi />
                <span className="text-[16px] font-bold text-[var(--jc-text)]">{c.response.name}</span>
              </div>
              <ul className="space-y-2">
                {c.response.lines.map((line, i) => (
                  <li key={i} className="flex items-start gap-2 text-[14.5px] text-[var(--jc-text)] leading-[1.45]">
                    <CheckCircle2 size={18} className="text-green-600 mt-0.5 flex-shrink-0" strokeWidth={2.4} />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-[14.5px] font-semibold text-green-600">{c.response.closing}</p>
            </div>
          </div>

          {/* 5 · Five core benefits — vertical stack */}
          <div className="px-5 pb-8 bg-white" data-testid="hiw-m-benefits">
            <ul className="space-y-5">
              {c.benefits.map((b, idx) => {
                const Icons = [Mic, CheckCircle2, Users, BarChart3, ShieldCheck];
                const Icon = Icons[idx];
                return (
                  <li key={idx} className="flex items-start gap-3.5">
                    <span
                      className="mt-0.5 inline-flex items-center justify-center w-11 h-11 rounded-lg bg-[var(--jc-blue)] text-white flex-shrink-0"
                      aria-hidden="true"
                    >
                      <Icon size={22} strokeWidth={2.2} />
                    </span>
                    <div className="min-w-0">
                      <div className="text-[17px] font-bold text-[var(--jc-text)] leading-tight">
                        {b.title}
                      </div>
                      <div className="mt-1 text-[14.5px] leading-[1.5] font-medium text-[var(--jc-text)]">
                        {b.body}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* 6 · Levi works across your entire business — full-width card */}
          <div className="px-5 pb-8 bg-white" data-testid="hiw-m-business">
            <div className="rounded-2xl bg-white shadow-xl border border-black/10 p-5">
              <h3 className="font-display font-bold text-[20px] leading-tight text-[var(--jc-text)]">
                {c.right_card.title_line_a} {c.right_card.title_line_b}
              </h3>
              <ul className="mt-4 space-y-3">
                {c.right_card.rows.map((r, i) => {
                  const Icon = r.Icon;
                  return (
                    <li key={i} className="flex items-center gap-3">
                      <span
                        className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[var(--jc-blue-soft)] text-[var(--jc-blue)] flex-shrink-0"
                        aria-hidden="true"
                      >
                        <Icon size={18} strokeWidth={2.2} />
                      </span>
                      <span className="text-[14.5px] font-semibold text-[var(--jc-text)]">
                        {r.label}
                      </span>
                    </li>
                  );
                })}
              </ul>
              <p className="mt-4 pt-4 border-t border-black/5 text-[13px] leading-[1.55] text-[var(--jc-text-soft)]">
                {c.right_card.footer}
              </p>
            </div>
          </div>

          {/* 7 · Bottom benefit strip — mobile vertical stack, same dark treatment */}
          <div className="bg-[#0A1128] text-white px-5 py-8" data-testid="hiw-m-strip">
            <ul className="space-y-5">
              {c.strip.map((s, i) => {
                const Icon = s.Icon;
                return (
                  <li key={i} className="flex items-start gap-3.5">
                    <span
                      className="mt-0.5 inline-flex items-center justify-center w-11 h-11 rounded-lg bg-white/10 text-[var(--jc-blue)] flex-shrink-0"
                      aria-hidden="true"
                    >
                      <Icon size={22} strokeWidth={2.4} />
                    </span>
                    <div className="min-w-0">
                      <div className="text-[16px] font-bold leading-tight">{s.title}</div>
                      <div className="mt-1 text-[14px] leading-[1.45] font-medium text-white/95">
                        {s.body}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </section>
    </>
  );
};

export default HowItWorksTopSection;
